import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { api } from "../lib/api";
import { Reveal, Eyebrow } from "./Reveal";

export default function LocationsSection() {
  const [offices, setOffices] = useState([]);
  const [selected, setSelected] = useState(0);

  useEffect(() => {
    api.get("/offices")
      .then((r) => setOffices(r.data.filter((o) => o.division === "jewellery" && o.maps_url)))
      .catch(() => {});
  }, []);

  const active = offices[selected];

  return (
    <section id="visit-us" className="bg-[#0B1320] border-y border-[#C5A059]/10 py-24 sm:py-32" data-testid="visit-us-section">
      <div className="max-w-[1400px] mx-auto px-5 sm:px-8">
        <Reveal>
          <Eyebrow>Visit Us</Eyebrow>
          <h2 className="font-serif font-light text-4xl sm:text-5xl lg:text-6xl tracking-tight text-[#FBF9F5] mt-4">
            Our Presence. <span className="italic text-[#C5A059]">Your Welcome.</span>
          </h2>
          <p className="text-base text-[#94A3B8] leading-loose mt-8 max-w-2xl">
            From the heart of Meerut's jewellery market to our retail and wholesale destinations, discover the
            places where relationships are built, business is conducted and generations of trust continue.
          </p>
        </Reveal>

        {offices.length > 0 && (
          <>
            <div className="flex gap-10 sm:gap-16 mt-14 sm:mt-16 overflow-x-auto border-b border-white/10" data-testid="location-tabs">
              {offices.map((o, i) => (
                <button
                  key={o.id}
                  onClick={() => setSelected(i)}
                  data-testid={`location-tab-${i}`}
                  className={`group relative shrink-0 text-left pb-5 transition-colors duration-500 ${
                    i === selected ? "text-[#C5A059]" : "text-[#94A3B8] hover:text-[#FBF9F5]"
                  }`}
                >
                  <span className="block text-[10px] tracking-[0.3em] font-semibold">{String(i + 1).padStart(2, "0")}</span>
                  <span className="block font-serif font-light text-xl sm:text-2xl mt-2 whitespace-nowrap">{o.name}</span>
                  <span
                    className={`absolute bottom-0 left-0 h-px bg-[#C5A059] transition-all duration-500 ${
                      i === selected ? "w-full" : "w-0"
                    }`}
                  />
                </button>
              ))}
            </div>

            {active && (
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 mt-12 lg:mt-16 items-center">
                <Reveal key={active.id} className="lg:col-span-4" data-testid="location-detail">
                  <p className="text-[11px] uppercase tracking-[0.3em] text-[#C5A059] font-semibold">
                    {active.department}
                  </p>
                  <h3 className="font-serif font-light text-3xl sm:text-4xl text-[#FBF9F5] mt-4 leading-tight">
                    {active.name}
                  </h3>
                  <p className="text-sm text-[#94A3B8] leading-relaxed mt-5">{active.address}</p>
                  <div className="flex flex-wrap gap-4 mt-10">
                    <a
                      href={active.maps_url}
                      target="_blank"
                      rel="noreferrer"
                      data-testid="view-on-map-btn"
                      className="text-[11px] uppercase tracking-[0.2em] font-semibold border border-white/20 text-[#FBF9F5] px-6 py-3.5 hover:border-[#C5A059] hover:text-[#C5A059] transition-colors duration-300"
                    >
                      View on Map
                    </a>
                    <a
                      href={active.maps_url}
                      target="_blank"
                      rel="noreferrer"
                      data-testid="get-directions-btn"
                      className="text-[11px] uppercase tracking-[0.2em] font-semibold bg-[#C5A059] text-[#060A12] px-6 py-3.5 hover:bg-[#d6b678] transition-colors duration-300"
                    >
                      Get Directions →
                    </a>
                  </div>
                </Reveal>
                <Reveal delay={0.08} className="lg:col-span-8">
                  <div className="border border-[#C5A059]/15 bg-[#060A12] p-2">
                    <iframe
                      key={active.id}
                      title={`Map — ${active.name}`}
                      src={active.embed_url}
                      className="w-full h-[360px] sm:h-[460px] border-0"
                      loading="lazy"
                      allowFullScreen
                      referrerPolicy="no-referrer-when-downgrade"
                      data-testid="map-frame"
                    />
                  </div>
                </Reveal>
              </div>
            )}
          </>
        )}

        <Reveal className="mt-24 sm:mt-28 border-t border-[#C5A059]/15 pt-14 sm:pt-16 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center" data-testid="builders-location-block">
          <div className="lg:col-span-8">
            <Eyebrow>RK Builders</Eyebrow>
            <h3 className="font-serif font-light text-3xl sm:text-4xl text-[#FBF9F5] mt-4">
              Building Communities Across a Growing Footprint
            </h3>
            <p className="text-sm text-[#94A3B8] leading-loose mt-5 max-w-xl">
              Planned residential societies, developed land, plotted developments and villas across multiple
              locations.
            </p>
          </div>
          <div className="lg:col-span-4 flex flex-col sm:flex-row lg:flex-col gap-5 lg:items-end">
            <Link to="/real-estate" data-testid="explore-rk-builders-btn" className="group inline-flex items-center gap-3">
              <span className="text-[11px] uppercase tracking-[0.25em] font-semibold text-[#FBF9F5] group-hover:text-[#C5A059] transition-colors duration-500">
                Explore RK Builders
              </span>
              <ArrowRight size={15} className="text-[#C5A059] transition-transform duration-500 group-hover:translate-x-1" />
            </Link>
            <Link to="/contact?type=property" data-testid="realestate-enquiries-btn" className="group inline-flex items-center gap-3">
              <span className="text-[11px] uppercase tracking-[0.25em] font-semibold text-[#94A3B8] group-hover:text-[#C5A059] transition-colors duration-500">
                Real Estate Enquiries
              </span>
              <ArrowRight size={15} className="text-[#C5A059] transition-transform duration-500 group-hover:translate-x-1" />
            </Link>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
